import { DefaultGameState, GameState } from "@shared/types";
import { css, html, htmlArray } from "../helpers/webComponents";
import { GameEventService } from "../services/GameEventService";
import { GameRouteService } from "../services/GameRouteService";
import { Page } from "../enums/Page";

/** CSS affecting the {@link MapComponent} */
const styles: string = css`
:host {
    z-index: 20;
    display: none;
}

#close {
    position: relative;
    
    max-height: 25px;
}


.map {
    position: relative;
    max-width: 100%;
    display: flex;
    justify-content: center;
}

.map img {
    width: 100%;
    height: auto;
}

#location {
    width: 1.5vw;
    height: 1.5vw;
    min-width: 15px;
    min-height: 15px;
    border-radius: 50%;
    background-color: red;
    position: absolute;
}
.button {
    background-color: #7f6ed7;
    border: 1px solid #332c57;
    padding: 5px 10px;
    margin: 0 0 0 10px;
    text-transform: uppercase;
    cursor: pointer;
    display: inline-block;
    user-select: none;
}

.button:hover {
    background-color: #332c57;
}
    
`;

export class MapComponent extends HTMLElement {
    /** Instance of the game event service */
    private readonly _gameEventService: GameEventService = new GameEventService();
    /** Instance of the game route service */
    private readonly _gameRouteService: GameRouteService = new GameRouteService();

    /** Current game state */
    private _currentGameState?: DefaultGameState;

    /**
     * The "constructor" of a Web Component
     */
    public connectedCallback(): void {
        this.attachShadow({ mode: "open" });

        void this.refreshGameState();
    }

    /**
     * Refresh the current game state
     */
    private async refreshGameState(): Promise<void> {
        const state: GameState = await this._gameRouteService.getGameState();
        this.updateGameState(state);
    }

    /**
         * Update the canvas to the provided game state
         *
         * @param state Game state to update the canvas to
         */
    private updateGameState(state: GameState): void {
        // Handle switching pages, if requested.
        if (state.type === "switch-page") {
            this._gameEventService.switchPage(state.page as Page);

            return;
        }

        // Reset the component
        this._currentGameState = state;

        // Refresh the web component
        this.render();
    }

    /**
     * Check what room the player is in and update it accordingly
     */
    private checkRoom(): void {
        const rooms: { [key: string]: string } = {
            "school-bathroom": "85%;18%;",
            "school-bathroom-monitor": "85%;18%;",

            "west-hallway-back": "87%;30%;",
            "west-hallway-front": "73%;30%;",
            "west-hallway-codelock": "73%;30%;",
            "west-hallway-pinboard": "87%;30%;",

            "Janitor-Room": "61%;18%;",
            "Kast-Janitor-Room": "61%;18%;",
            "Hammer-Room": "61%;18%;",

            "cafeteria": "39%;30%;",
            "cafeteria-closet": "39%;30%;",
            "cafeteria-table": "39%;30%;",
            "generator": "39%;30%;",

            "east-hallway": "39%;55%;",
            "east-hallway-basement": "73%;55%;",
            "east-hallway-principal": "39%;55%;",
            "east-hallway-phone": "73%;55%;",

            "school-basement": "80%;75%;",
            "SchoolBasementSally": "80%;75%;",
            "school-basement-kast": "80%;75%;",

            "principal-room": "36%;71%;",
            "principal-room-back": "36%;71%;",
            "books-room": "36%;71%;",
            "drawers-room": "36%;71%;",
            "eye-test-room": "36%;71%;",
            "lamp-room": "36%;71%;",
            "principal-to-west-hall-door": "36%;71%;",
            "radio-room": "36%;71%;",
            "writing-room": "36%;71%;",

            "north-hallway-back": "12%;60%;",
            "north-hallway-front": "12%;30%;",

            "outside": "11.5%;12%;",
        };

        setTimeout(() => {
            // Display red dot in location relevant to the location the player is in
            Object.entries(rooms).forEach(([room, position]) => {
                if (this._currentGameState?.roomAlias === room) {
                    const redDot: HTMLDivElement = this.shadowRoot!.getElementById("location") as HTMLDivElement;
                    const splitPosition: string[] = position.split(";");
                    redDot.style.top = splitPosition[0];
                    redDot.style.left = splitPosition[1];
                }
            });
        });
    }

    /**
     * Render the contents of this page
     */
    private render(): void {
        if (!this.shadowRoot) {
            return;
        }

        const elements: HTMLElement[] = htmlArray`
            <style>
                ${styles}
            </style>

            ${this.renderMap()}
        `;

        while (this.shadowRoot.firstChild) {
            this.shadowRoot.firstChild.remove();
        }

        this.shadowRoot.append(...elements);
    }

    private renderMap(): HTMLElement {
        this.checkRoom();
        // Set timeout to make changes to map frontend after its loaded in
        setTimeout(() => {
            const closeDiv: HTMLDivElement = this.shadowRoot!.getElementById("close") as HTMLDivElement;
            closeDiv.addEventListener("click", () => {
                this.style.display = "none";
            });
        });
        return html`
                    <section class="map">
                                <img src="/assets/img/components/mapcomponent.png" />
                                <div id="location"></div>
                                <div id="close" class="button">Close</div>
                    </section>
                    
        `;
    }
}
